import { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faLock,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import cloneDeep from "lodash.clonedeep";
import {
  draft,
  draftKey,
  showConflicts,
  useDraftConflicts,
  useDraftCount,
  useDraftSave,
  useDraftSaving
} from "../../hooks/draft";
import { merge } from "../../hooks/draft/diff";
import { useHasPendingEdits } from "../../hooks/usePendingEdits";
import { useMainContext } from "../../hooks/useMainContext";
import useUserFields from "../../hooks/useUserFields";
import {
  RebaseModal,
  dependentsSentence,
  isDeleted,
  isTaken,
  rebaseFailure,
  rebasePlan,
  wholeDraftsOf
} from "./conflictDialog";
import styles from "./styles.module.scss";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// My row of a live conflict: the journal's current (null: I deleted it)
const journalMineOf = (scope) => (accessor, id) => {
  const key = draftKey(accessor, id);
  const entry = draft
    .entries(scope)
    .find((e) => draftKey(e.accessor, e.id) === key);
  if (!entry) return undefined;
  return entry.deleted ? null : cloneDeep(entry.current);
};

const conflictKey = ({ accessor, id }) => draftKey(accessor, id);

// "N unsaved changes · Save · Discard" for the draft of a scope; nothing while
// the draft is empty. Save is disabled on main and while an edit of the form
// is not handed over yet (inside an Item). embedded: inside an Item whose
// Save saves the draft (draftScope), the bar has no Save of its own, and
// formToDiscard() gives the reset of the form when it is coupled with the
// draft (the two halves of an operation go together), else null.
// A live conflict (someone saved an item of the draft meanwhile) shows a
// warning: the save will be refused; "Show changes" opens the conflict
// dialog (what changed, mine from the journal); "Reload them" gives up those
// items (draft.reloadConflicts). When the draft adds, copies or deletes
// variants or pages together with one of them, the whole draft is discarded
// instead (the confirmation says so, as the conflict dialog does). Inside an
// Item, formAfterReload(conflicts), asked before the reload, gives what its
// form does after it, as the Item's conflict dialog (with the rows
// discarded whole); without it, the form goes with a whole draft discarded
// when it is coupled with the draft or its row is in it (formToDiscard).
// "Apply mine on theirs" (every row merges: no field changed by both,
// nothing deleted, no row created in the draft; not on main): a preview,
// then draft.rebaseConflicts. Discard, Reload and Apply are disabled while a
// save of the scope runs.
// Inside an Item: onRebase(items) and mineOf(accessor, id), the Item's (as
// for the dialog of its Save). Mine is then the form's values for its row
// (drafted, then opened again and typed in: the journal's row is older),
// and onRebase rebases the rows of the draft and sets the form on its
// merged row (it says so); without them, mine is the journal's row and the
// bar rebases the draft (draft.rebaseConflicts). With the Item's mineOf the
// rebase is planned on the click (the values change at every keystroke):
// Apply is offered unless a row is deleted, and its window shows the
// preview or why it can't.
export default function SaveBar({
  scope,
  embedded = false,
  formToDiscard,
  formAfterReload,
  onRebase,
  mineOf: givenMineOf,
  className = ""
}) {
  const count = useDraftCount(scope);
  const conflicts = useDraftConflicts(scope);
  // A save of the scope runs (the bar's, or the Item's that sends the draft)
  const scopeSaving = useDraftSaving(scope);
  const pending = useHasPendingEdits();
  const { branch } = useUserFields("branch");
  const { requestConfirm, createNotification } = useMainContext();
  const saveDraft = useDraftSave(scope);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rebaseError, setRebaseError] = useState(null);
  const busy = saving || scopeSaving;
  const onMain = branch === "main";

  const mineOf = useMemo(
    () => givenMineOf ?? journalMineOf(scope),
    [givenMineOf, scope]
  );
  const canRebase =
    conflicts.length > 0 &&
    !onMain &&
    Boolean(onRebase ?? draft.rebaseConflicts);
  // Mine from the journal: the plan follows the revisions of the rows in
  // conflict (an operation on one of them), not every change of the draft,
  // and Apply is offered when every row merges. Mine from the Item (the
  // form's values, which change at every keystroke): no plan before the
  // click (askRebase plans on the values of then: the preview, or why it
  // can't, as the banner's), Apply offered unless a row is deleted.
  const planOnClick = Boolean(givenMineOf);
  const revs = useSyncExternalStore(draft.subscribe, () =>
    canRebase && !planOnClick
      ? conflicts
          .map((conflict) => {
            const key = conflictKey(conflict);
            return `${key}@${draft.revsOf([key]).get(key)}`;
          })
          .join(",")
      : null
  );
  const plan = useMemo(
    () => (revs === null ? null : rebasePlan(conflicts, mineOf)),
    [conflicts, mineOf, revs]
  );
  const offerRebase = planOnClick
    ? canRebase &&
      conflicts.every((conflict) => !isDeleted(conflict) && !isTaken(conflict))
    : Boolean(plan?.ok);

  const save = useCallback(() => {
    setSaving(true);
    saveDraft().finally(() => setSaving(false));
  }, [saveDraft]);

  const discard = useCallback(() => {
    const resetForm = formToDiscard?.();

    requestConfirm(
      `Discard ${plural(count, "unsaved change")}${
        resetForm ? " and the changes of the form" : ""
      }? The stored items are shown again.`,
      () => {
        draft.discard(scope);
        resetForm && resetForm();
      }
    );
  }, [count, formToDiscard, requestConfirm, scope]);

  // Gives up the live conflicts of the scope (see the comment above)
  const reloadNow = useCallback(() => {
    // Asked before the reload: the form's row may leave the draft
    const afterReload = formAfterReload?.(conflicts);
    const resetForm = afterReload ? null : formToDiscard?.();
    const { discarded = [] } = draft.reloadConflicts(scope) ?? {};
    if (afterReload) afterReload(discarded);
    else if (discarded.includes(scope)) resetForm && resetForm();
  }, [conflicts, formAfterReload, formToDiscard, scope]);

  const reload = useCallback(() => {
    const whole = wholeDraftsOf(conflicts);

    requestConfirm(
      `Discard your changes to the ${plural(conflicts.length, "item")} saved by someone else, and show them as they are stored now?${
        whole.length ? ` ${dependentsSentence(whole, "Reloading them")}` : ""
      }`,
      reloadNow
    );
  }, [conflicts, reloadNow, requestConfirm]);

  // My changes on their version (items: rebasePlan's): the store merges the
  // rows again on what it has now, and refuses those that no longer merge.
  // The Item's onRebase when given (it says what it applied)
  const rebaseNow = useCallback(
    (items) => {
      if (onRebase) return onRebase(items);
      const result = draft.rebaseConflicts(
        scope,
        items.map(conflictKey),
        merge
      );
      if (!rebaseFailure(result) && result?.rebased?.length)
        createNotification(
          "success",
          `Your changes were applied on their version of ${plural(
            result.rebased.length,
            "item"
          )}: save to keep them`
        );
      return result;
    },
    [createNotification, onRebase, scope]
  );

  const showChanges = useCallback(
    () =>
      showConflicts({
        conflicts,
        onDiscard: reloadNow,
        onRebase: canRebase ? rebaseNow : undefined,
        mineOf
      }),
    [canRebase, conflicts, mineOf, rebaseNow, reloadNow]
  );

  const askRebase = useCallback(() => {
    setRebaseError(null);
    // My rows of now
    setPreview(rebasePlan(conflicts, mineOf));
  }, [conflicts, mineOf]);

  const confirmRebase = useCallback(
    (items) => {
      Promise.resolve(rebaseNow(items))
        .then((result) => {
          const refused = rebaseFailure(result);
          if (refused) setRebaseError(refused);
          else setPreview(null);
        })
        .catch((error) => {
          console.error("⚠️ REBASE OF THE DRAFT", error);
          setRebaseError(String(error?.message ?? error));
        });
    },
    [rebaseNow]
  );

  if (count === 0) return null;

  const who =
    [
      ...new Set(
        conflicts.map(({ lastSaved }) => lastSaved?.username).filter(Boolean)
      )
    ].join(", ") || "Someone";

  return (
    <div className={`${styles.saveBar} ${className}`}>
      {conflicts.length > 0 ? (
        <span className={styles.conflicts}>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className={styles.conflictIcon}
          />
          {who} changed {plural(conflicts.length, "item")} you are editing: your
          save will be refused
          <Button
            size="sm"
            variant="outline-secondary"
            className={styles.reload}
            onClick={showChanges}
          >
            Show changes
          </Button>
          {offerRebase ? (
            <Button
              size="sm"
              variant="outline-primary"
              onClick={askRebase}
              disabled={busy}
            >
              Apply mine on theirs
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline-warning"
            onClick={reload}
            disabled={busy}
          >
            Reload them
          </Button>
        </span>
      ) : null}
      <span className={styles.count}>{plural(count, "unsaved change")}</span>
      {embedded ? null : (
        <Button
          size="sm"
          variant={onMain ? "outline-success" : "success"}
          className={styles.save}
          disabled={onMain || pending || busy}
          onClick={save}
        >
          {saving ? (
            <FontAwesomeIcon icon={faSpinner} pulse />
          ) : onMain ? (
            <FontAwesomeIcon icon={faLock} />
          ) : (
            "Save"
          )}
        </Button>
      )}
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={busy}
        onClick={discard}
      >
        Discard
      </Button>
      <RebaseModal
        plan={preview}
        failure={rebaseError}
        onConfirm={confirmRebase}
        onCancel={() => setPreview(null)}
      />
    </div>
  );
}
